import React, { useState, useEffect } from 'react';
import { FileText, Check, Clock, ChevronRight, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CharterCard = () => {
  const navigate = useNavigate();
  const [charter, setCharter] = useState(null);
  const [charterPdf, setCharterPdf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.request('/api/family/charter').catch(() => null),
      apiClient.request('/api/family/charter/my-pdf').catch(() => null),
    ]).then(([charterData, pdfData]) => {
      setCharter(charterData);
      if (pdfData?.charter_pdfs?.length > 0) {
        setCharterPdf(pdfData.charter_pdfs[0]);
      }
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card" data-testid="charter-card">
        <div className="card-header">
          <h2 className="card-title">Charter</h2>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (!charter) {
    return (
      <div className="card" data-testid="charter-card">
        <div className="card-header">
          <h2 className="card-title">Charter</h2>
        </div>
        <div className="text-center py-6">
          <FileText size={32} className="mx-auto text-purple-300 mb-3" />
          <p className="text-sm text-gray-700 font-medium">No charter yet</p>
          <p className="text-xs text-gray-600 mt-1.5 mb-4">Create a parent-teen agreement</p>
          {charterPdf && (
            <a
              href={`${API_URL}${charterPdf.pdf_url}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="charter-card-pdf-download"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5831F] text-white text-sm font-semibold rounded-lg hover:bg-[#E07318] mb-3"
            >
              <Download size={16} /> Download Charter PDF
            </a>
          )}
          <button
            onClick={() => navigate('/charter')}
            data-testid="charter-card-create-btn"
            className="block mx-auto px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg"
          >
            Create Charter
          </button>
        </div>
      </div>
    );
  }

  const isActive = charter.status === 'active';
  const isPending = charter.status?.startsWith('pending');

  return (
    <div className="card" data-testid="charter-card">
      <div className="card-header">
        <h2 className="card-title">Charter</h2>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
        }`}>
          {isActive ? 'Active' : 'Pending'}
        </span>
      </div>

      <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100 mb-3">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
            <FileText size={20} className="text-white" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">HiBFF Family Charter</p>
            <p className="text-xs text-gray-600">
              {charter.parent_name} & {charter.teen_name}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="flex items-center gap-1.5">
            {charter.parent_signed
              ? <Check size={14} className="text-green-500" />
              : <Clock size={14} className="text-yellow-500" />}
            <span className="text-xs text-gray-700">Parent: {charter.parent_signed ? 'Signed' : 'Pending'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {charter.teen_signed
              ? <Check size={14} className="text-green-500" />
              : <Clock size={14} className="text-yellow-500" />}
            <span className="text-xs text-gray-700">Teen: {charter.teen_signed ? 'Signed' : 'Pending'}</span>
          </div>
        </div>

        {charter.tech_free_zones?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {charter.tech_free_zones.slice(0, 3).map((zone, i) => (
              <span key={i} className="px-2 py-0.5 bg-white/80 text-purple-700 rounded-full text-[10px]">{zone}</span>
            ))}
          </div>
        )}
      </div>

      {charterPdf && (
        <a
          href={`${API_URL}${charterPdf.pdf_url}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="charter-card-pdf-download"
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-[#F5831F] rounded-lg hover:bg-[#E07318] transition-colors mb-2"
        >
          <Download size={16} /> Download Charter PDF
        </a>
      )}

      <button
        onClick={() => navigate('/charter')}
        data-testid="charter-card-view-btn"
        className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
      >
        {isPending ? 'Sign Charter' : 'View Charter'}
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

export default CharterCard;
